import React, { useState } from 'react';
import { View, Image, Pressable, Text, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Series } from '../types/domain';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface FeaturedBannersProps {
  series: Series[];
  isLoading?: boolean;
}

export function FeaturedBanners({
  series,
  isLoading = false,
}: FeaturedBannersProps): JSX.Element | null {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  const withBanners = series.filter(s => s.banner_url);

  if (withBanners.length === 0 || isLoading) {
    return null;
  }

  const current = withBanners[activeIndex];

  return (
    <View style={styles.container}>
      <Pressable
        style={styles.bannerWrapper}
        onPress={() => navigate(`/series/${current.id}`)}
      >
        <Image
          source={{ uri: current.banner_url }}
          style={styles.banner}
        />
        <View style={styles.overlay} />
        <View style={styles.gradientOverlay} />
        <View style={styles.content}>
          <Text style={styles.title}>{current.title}</Text>
          <Text style={styles.description} numberOfLines={3}>
            {current.description}
          </Text>
          <View style={styles.buttons}>
            <Pressable
              style={styles.watchBtn}
              onPress={() => navigate(`/watch/${current.id}/1`)}
            >
              <Play size={16} color={NETFLIX_COLORS.white} />
              <Text style={styles.watchBtnText}>ASSISTIR</Text>
            </Pressable>
            <Pressable
              style={styles.detailsBtn}
              onPress={() => navigate(`/series/${current.id}`)}
            >
              <Text style={styles.detailsBtnText}>DETALHES</Text>
            </Pressable>
          </View>
        </View>
      </Pressable>

      {withBanners.length > 1 && (
        <View style={styles.indicators}>
          {withBanners.map((_, index) => (
            <Pressable
              key={index}
              style={[
                styles.indicator,
                index === activeIndex && styles.indicatorActive,
              ]}
              onPress={() => setActiveIndex(index)}
            />
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: NETFLIX_SPACING.xxxl,
  },
  bannerWrapper: {
    position: 'relative',
    height: 400,
    borderRadius: 8,
    overflow: 'hidden',
  },
  banner: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  gradientOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 200,
    backgroundColor: 'rgba(0,0,0,0.8)',
  },
  content: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: NETFLIX_SPACING.xl,
    paddingTop: NETFLIX_SPACING.xxxl,
  },
  title: {
    color: NETFLIX_COLORS.white,
    fontSize: 28,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.md,
  },
  description: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 13,
    lineHeight: 20,
    marginBottom: NETFLIX_SPACING.lg,
  },
  buttons: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
  },
  watchBtn: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: 12,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.sm,
  },
  watchBtnText: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '700',
  },
  detailsBtn: {
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.white,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: 12,
    borderRadius: 4,
  },
  detailsBtnText: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '700',
  },
  indicators: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.sm,
    justifyContent: 'center',
    marginTop: NETFLIX_SPACING.lg,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: NETFLIX_COLORS.textTertiary,
  },
  indicatorActive: {
    backgroundColor: NETFLIX_COLORS.primary,
    width: 20,
  },
});
